/**
 * Module-level Stores für den Debug-Layer.
 *
 * Bewusst React-unabhängig, damit non-React-Code (Error-Boundary,
 * Reporter, Edge-Function-Wrapper) Entries hinzufügen kann, ohne Hooks.
 *
 * Persistenz: KEINE. Beide Stores leben ausschliesslich im Speicher der
 * aktuellen Tab-Session. Ein Reload setzt alles auf Defaults zurück.
 */

import type {
  DebugLayerEntry,
  DebugActionDefinition,
  PreviewFlagDefinition,
  PreviewFlagState,
} from './types';

// ── Entries ────────────────────────────────────────────────────────────

const entries = new Map<string, DebugLayerEntry>();
const entryListeners = new Set<() => void>();

function emitEntries() {
  for (const cb of entryListeners) cb();
}

export function addDebugEntry(entry: DebugLayerEntry): void {
  entries.set(entry.id, {
    ...entry,
    createdAt: entry.createdAt ?? new Date().toISOString(),
  });
  emitEntries();
}

export function removeDebugEntry(id: string): void {
  if (entries.delete(id)) emitEntries();
}

export function clearDebugEntries(): void {
  if (entries.size === 0) return;
  entries.clear();
  emitEntries();
}

export function listDebugEntries(): DebugLayerEntry[] {
  return Array.from(entries.values());
}

export function subscribeEntries(cb: () => void): () => void {
  entryListeners.add(cb);
  return () => {
    entryListeners.delete(cb);
  };
}

// ── Preview-Flags ──────────────────────────────────────────────────────

const flagDefs = new Map<string, PreviewFlagDefinition>();
const flagValues = new Map<string, boolean>();
const flagListeners = new Set<() => void>();

function emitFlags() {
  for (const cb of flagListeners) cb();
}

/**
 * Registriert eine Preview-Flag-Definition. Idempotent: doppelte
 * Registrierungen überschreiben nur die Metadaten, nicht den aktuellen
 * boolean-Wert.
 *
 * Default ist IMMER `false`.
 */
export function definePreviewFlag(def: PreviewFlagDefinition): void {
  flagDefs.set(def.id, def);
  if (!flagValues.has(def.id)) flagValues.set(def.id, false);
  emitFlags();
}

export function getPreviewFlag(id: string): boolean {
  return flagValues.get(id) ?? false;
}

export function setPreviewFlag(id: string, value: boolean): void {
  // Unbekannte IDs werden ignoriert, damit kein "Geister-Flag" entsteht.
  if (!flagDefs.has(id)) return;
  const prev = flagValues.get(id) ?? false;
  if (prev === value) return;
  flagValues.set(id, value);
  emitFlags();
}

export function listPreviewFlags(): PreviewFlagState[] {
  return Array.from(flagDefs.values()).map((d) => ({
    ...d,
    enabled: flagValues.get(d.id) ?? false,
  }));
}

export function activePreviewFlagCount(): number {
  let n = 0;
  for (const v of flagValues.values()) if (v) n += 1;
  return n;
}

export function subscribePreviewFlags(cb: () => void): () => void {
  flagListeners.add(cb);
  return () => {
    flagListeners.delete(cb);
  };
}

// ── Admin-Actions (route-scoped) ───────────────────────────────────────

const actions = new Map<string, DebugActionDefinition>();
const actionListeners = new Set<() => void>();

function emitActions() {
  for (const cb of actionListeners) cb();
}

export function defineDebugAction(action: DebugActionDefinition): void {
  actions.set(action.id, action);
  emitActions();
}

export function removeDebugAction(id: string): void {
  if (actions.delete(id)) emitActions();
}

export function listDebugActions(): DebugActionDefinition[] {
  return Array.from(actions.values());
}

export function subscribeDebugActions(cb: () => void): () => void {
  actionListeners.add(cb);
  return () => {
    actionListeners.delete(cb);
  };
}

// ── Test-Helper ────────────────────────────────────────────────────────

/** NUR FÜR TESTS: setzt sämtlichen Layer-State zurück. */
export function __resetDebugLayerForTests(): void {
  entries.clear();
  flagDefs.clear();
  flagValues.clear();
  actions.clear();
  emitEntries();
  emitFlags();
  emitActions();
}
